import React, { type ReactNode } from 'react';
import { Box, type BoxProps, type SxProps, type Theme } from '@mui/material';

/**
 * 弹窗内表单/控件区域的浅底衬（与多选标签弹窗一致：中性浅底、内边距、圆角），便于在主题色 `fill-primary-200` 的 Paper 上形成「白底/浅灰底」内容块。
 * @param fullHeight - 为 true 时拉满 `DialogContent` 的可视高度（需配合 `DialogComponent` 的 `heightSize` 与 `getDialogFormBodyContentSx` 使用）
 */
export type DialogFormContentSurfaceProps = {
    children: ReactNode;
    /** 是否占满内容区高度，多选居中或首屏长表单与固定高度弹窗时通常为 true */
    fullHeight?: boolean;
} & Omit<BoxProps, 'children'>;

/**
 * 与 `heightSize` 及浅底 `DialogFormContentSurface` 配合：将 Paper 与 `DialogContent` 竖向 flex，使内容区可伸缩、底部按钮区固定。
 * @param contentVertical - `center`：单控件竖直居中（多选弹窗）；`start`：表单项自顶对齐
 */
export function getDialogFormBodyContentSx(
    contentVertical: 'center' | 'start'
): SxProps<Theme> {
    return {
        '.MuiDialog-container > .MuiPaper-root': {
            display: 'flex',
            flexDirection: 'column',
        },
        '& .MuiDialogContent-root': {
            flex: '1 1 0',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: contentVertical === 'center' ? 'center' : 'flex-start',
            alignItems: 'stretch',
        },
    };
}

const baseSurfaceSx: SxProps<Theme> = {
    width: '100%',
    backgroundColor: 'var(--fill-neutral-100)',
    padding: '16px',
    borderRadius: '8px',
};

/**
 * 弹窗 `DialogContent` 内的统一表单衬底容器。
 */
export default function DialogFormContentSurface(props: DialogFormContentSurfaceProps) {
    const { children, fullHeight = false, sx, ...rest } = props;

    return (
        <Box
            {...rest}
            sx={[
                baseSurfaceSx,
                fullHeight
                    ? {
                        height: '100%',
                        minHeight: 0,
                        // 长表单项时允许在浅底块内滚动
                        overflow: 'auto',
                    }
                    : false,
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            {children}
        </Box>
    );
}
